import { MusicIcon } from '../components/icons/MusicIcon';
import { RoomName } from '../components/icons/RoomName';
import RoomVuMeter from '../components/RoomVuMeter';
import { buildRoomInvitation } from '../services/roomInvitation';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';
import { 
  Music, 
  Send, 
  Check, 
  Clock,
  Users,
  Volume2,
  VolumeX,
  Sparkles,
  Skull
} from 'lucide-react';
import confetti from 'canvas-confetti';
import PowerUpsMenu from '../components/PowerUpsMenu';
import PowerUpEffects, { usePowerUpEffects } from '../components/PowerUpEffects';
import QuizMode from '../components/game/QuizMode';
import BattleRoyaleStatus from '../components/game/BattleRoyaleStatus';
import TeamScoreDisplay from '../components/game/TeamScoreDisplay';
import GameModeBadge from '../components/game/GameModeBadge';
import AuthModal from '../components/AuthModal';
import { playRoundMedia, playTestTone } from '../services/audioPlayback';
import { ScenePlayerBoard } from '../components/scene/ScenePlayerBoard';
import { SceneRoundView } from '../components/scene/SceneRoundView';
import { SceneResultView } from '../components/scene/SceneResultView';
import '../styles/scene-game.css';

// Convertir le slider (0-100) vers volume audio (0-1) avec courbe logarithmique
function sliderToVolume(sliderValue: number): number {
  // Courbe exponentielle pour une perception linéaire du volume
  // slider 0 = volume 0, slider 100 = volume 1
  if (sliderValue <= 0) return 0;
  if (sliderValue >= 100) return 1;
  // Utilise une courbe exponentielle: volume = (e^(x*3) - 1) / (e^3 - 1)
  const vol = (Math.exp(sliderValue / 100 * 3) - 1) / (Math.E ** 3 - 1);
  return Math.min(1, Math.max(0, vol)); // Clamp entre 0 et 1
}

// Hook pour le volume persistant
function useVolume() {
  // Stocke la valeur du slider (0-100) pour l'UI
  const [sliderValue, setSliderValue] = useState(() => {
    const saved = localStorage.getItem('blindtest-volume-slider');
    return saved ? parseFloat(saved) : 50; // 50% par défaut (environ 0.35 en volume réel)
  });
  
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem('blindtest-muted') === 'true';
  });
  
  // Volume réel calculé à partir du slider
  const volume = sliderToVolume(sliderValue);
  
  const setVolume = useCallback((sliderVal: number) => {
    setSliderValue(sliderVal);
    localStorage.setItem('blindtest-volume-slider', String(sliderVal));
  }, []);
  
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      localStorage.setItem('blindtest-muted', String(!prev));
      return !prev;
    });
  }, []);
  
  return { volume, sliderValue, setVolume, isMuted, toggleMute };
}

// Créer un contexte audio global (pour les sons de feedback uniquement)
let audioContext: AudioContext | null = null;

function getAudioContext() {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
}

// Hook pour la normalisation audio avec DynamicsCompressor
// Égalise le volume entre les différentes chansons
function useAudioNormalization(audioRef: React.RefObject<HTMLAudioElement>, volume: number, isMuted: boolean) {
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const isConnectedRef = useRef(false);
  
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const setupAudioPipeline = () => {
      if (isConnectedRef.current) return;
      
      try {
        const ctx = getAudioContext();
        if (ctx.state === 'suspended') {
          void ctx.resume().catch(() => {});
        }
        
        // Créer les nodes seulement une fois
        if (!analyserRef.current) {
          try {
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 128;
            analyser.smoothingTimeConstant = .75;
            analyserRef.current = analyser;
          } catch { /* Keep the existing audible output if analysis is unavailable. */ }
        }
        if (!sourceRef.current) {
          sourceRef.current = ctx.createMediaElementSource(audio);
        }
        
        if (!gainRef.current) {
          gainRef.current = ctx.createGain();
        }
        
        if (!compressorRef.current) {
          compressorRef.current = ctx.createDynamicsCompressor();
          // Configuration du compresseur pour normaliser le volume
          compressorRef.current.threshold.setValueAtTime(-24, ctx.currentTime); // Seuil bas pour capter les variations
          compressorRef.current.knee.setValueAtTime(30, ctx.currentTime);
          compressorRef.current.ratio.setValueAtTime(12, ctx.currentTime); // Ratio élevé pour forte compression
          compressorRef.current.attack.setValueAtTime(0.003, ctx.currentTime);
          compressorRef.current.release.setValueAtTime(0.25, ctx.currentTime);
        }
        
        // Connecter: Source -> Compressor -> Gain -> Destination
        sourceRef.current.connect(compressorRef.current);
        compressorRef.current.connect(gainRef.current);
        if (analyserRef.current) {
          gainRef.current.connect(analyserRef.current);
          analyserRef.current.connect(ctx.destination);
        } else gainRef.current.connect(ctx.destination);
        
        isConnectedRef.current = true;
        console.log('🔊 Audio normalization pipeline connecté');
      } catch (e) {
        console.error('Erreur setup audio normalization:', e);
      }
    };
    
    // Setup au premier play
    const handlePlay = () => {
      setupAudioPipeline();
    };
    
    audio.addEventListener('play', handlePlay);
    
    return () => {
      audio.removeEventListener('play', handlePlay);
    };
  }, [audioRef]);
  
  // Mettre à jour le gain quand le volume change
  useEffect(() => {
    if (gainRef.current) {
      const ctx = getAudioContext();
      const targetGain = isMuted ? 0 : volume;
      gainRef.current.gain.setValueAtTime(targetGain, ctx.currentTime);
    }
  }, [volume, isMuted]);
  return analyserRef;
}

// Hook pour les sons de feedback - accepte volume et isMuted en paramètres
function useSoundEffects(volume: number, isMuted: boolean) {
  
  const playSuccess = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      
      const now = ctx.currentTime;
      
      // Son de succès (arpège montant Do-Mi-Sol)
      [523.25, 659.25, 783.99].forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, now + i * 0.1);
        gainNode.gain.setValueAtTime(volume * 0.3, now + i * 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.1 + 0.2);
        oscillator.start(now + i * 0.1);
        oscillator.stop(now + i * 0.1 + 0.2);
      });
    } catch (e) {
      console.error('Error playing success sound:', e);
    }
  }, [volume, isMuted]);
  
  const playError = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      
      const now = ctx.currentTime;
      
      // Son d'erreur (buzzer descendant)
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sawtooth';
      oscillator.frequency.setValueAtTime(200, now);
      oscillator.frequency.exponentialRampToValueAtTime(100, now + 0.2);
      gainNode.gain.setValueAtTime(volume * 0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      oscillator.start(now);
      oscillator.stop(now + 0.3);
    } catch (e) {
      console.error('Error playing error sound:', e);
    }
  }, [volume, isMuted]);
  
  const playCountdown = useCallback(() => {
    if (isMuted) return;
    
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
      
      const now = ctx.currentTime;
      
      // Son de countdown (bip court)
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, now);
      gainNode.gain.setValueAtTime(volume * 0.2, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      oscillator.start(now);
      oscillator.stop(now + 0.1);
    } catch (e) {
      console.error('Error playing countdown sound:', e);
    }
  }, [volume, isMuted]);
  
  return { playSuccess, playError, playCountdown };
}

export default function GamePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { player, isAuthenticated } = useAuthStore();
  const { 
    currentRoom, hasLeftRoom, isConnected,
    currentRound,
    roundResult,
    gameResult,
    scores,
    myAnswer,
    answerFeedback, resultSaves, resultWasGuest, resultPlayerId,
    submitAnswer,
    submitQuizAnswer,
    resetGame, leaveRoom,
    isWaitingForPlayers,
    playerAnswers,
    // Mode de jeu
    gameMode,
    modeConfig,
    currentQuestion,
    quizAnswered: _quizAnswered,
    playerLives: _playerLives,
    isEliminated,
    teams: _teams,
    teamScores: _teamScores,
  } = useSocketStore();
  
  const [showRegister, setShowRegister] = useState(false);
  const [shareFallback, setShareFallback] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [themeLeave, setThemeLeave] = useState<'idle' | 'pending' | 'error' | 'departed'>('idle');
  const [audioStatus, setAudioStatus] = useState<'idle' | 'started' | 'blocked' | 'unavailable'>('idle');
  const [toneStatus, setToneStatus] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [progress, setProgress] = useState(100);
  const [shakeInput, setShakeInput] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [prevTitleFound, setPrevTitleFound] = useState(false);
  const [prevArtistFound, setPrevArtistFound] = useState(false);
  const [showPowerUps, setShowPowerUps] = useState(false);  // Toggle power-ups
  
  // Power-up effects (frozen state)
  const { isFrozen } = usePowerUpEffects();
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const activeRoundRef = useRef(currentRound);
  activeRoundRef.current = currentRound;
  
  const { volume, sliderValue, setVolume, isMuted, toggleMute } = useVolume();
  const { playSuccess, playError, playCountdown } = useSoundEffects(volume, isMuted);
  
  // Normalisation audio avec compresseur dynamique
  const analyserRef = useAudioNormalization(audioRef as React.RefObject<HTMLAudioElement>, volume, isMuted);
  
  // Refs pour avoir accès aux valeurs actuelles dans les callbacks
  const volumeRef = useRef(volume);
  const isMutedRef = useRef(isMuted);
  
  // Mettre à jour les refs quand les valeurs changent
  useEffect(() => {
    volumeRef.current = volume;
    isMutedRef.current = isMuted;
  }, [volume, isMuted]);
  
  // Appliquer le volume à l'audio quand il change
  useEffect(() => {
    if (audioRef.current) {
      const newVolume = isMuted ? 0 : volume;
      audioRef.current.volume = newVolume;
      console.log('Volume appliqué:', newVolume);
    }
  }, [volume, isMuted]);
  
  const countdownSoundRef = useRef(playCountdown);
  countdownSoundRef.current = playCountdown;
  const startMedia = useCallback(async (round: NonNullable<typeof currentRound>, retry = false) => {
    const media = audioRef.current;
    if (!media || round.isDeafMode || activeRoundRef.current !== round) return;
    if (!round.previewUrl) { setAudioStatus('unavailable'); return; }
    media.volume = isMutedRef.current ? 0 : volumeRef.current;
    const status = await playRoundMedia(media, round.previewUrl, async () => {
      const ctx = getAudioContext(); if (ctx.state === 'suspended') await ctx.resume();
    }, () => activeRoundRef.current === round, { retry });
    if (status !== 'stale') setAudioStatus(status);
  }, []);

  // Only server round identity changes this effect. Volume and mute use refs.
  useEffect(() => {
    const round = currentRound;
    const media = audioRef.current;
    if (!round) { media?.pause(); return; }
    setLastAnswerCorrect(null); setPrevTitleFound(false); setPrevArtistFound(false);
    setShowCountdown(true); setCountdown(3); setProgress(100); setAnswer('');
    setAudioStatus(!round.isDeafMode && !round.previewUrl ? 'unavailable' : 'idle');
    media?.pause();
    let count = 3;
    countdownSoundRef.current();
    const timer = setInterval(() => {
      if (activeRoundRef.current !== round) return;
      count--; setCountdown(count);
      if (count > 0) countdownSoundRef.current();
      else { clearInterval(timer); setShowCountdown(false); void startMedia(round); }
    }, 1000);
    return () => { clearInterval(timer); media?.pause(); media?.removeAttribute('src'); };
  }, [currentRound, startMedia]);

  // Timer principal
  useEffect(() => {
    if (currentRound && !showCountdown) {
      setTimeLeft(currentRound.duration);
      setAnswer('');
      inputRef.current?.focus();
      
      // Timer pour le temps restant
      const interval = setInterval(() => {
        setTimeLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
      
      // Timer pour la barre de progression (plus fluide)
      const startTime = Date.now();
      const duration = currentRound.duration * 1000;
      
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      progressIntervalRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);
      }, 50);
      
      return () => {
        clearInterval(interval);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [currentRound, showCountdown]);
  
  useEffect(() => {
    const resumeAudio = () => {
      if (audioContext?.state === 'suspended') void audioContext.resume().catch(() => setAudioStatus('blocked'));
    };
    document.addEventListener('click', resumeAudio); document.addEventListener('keydown', resumeAudio);
    return () => { document.removeEventListener('click', resumeAudio); document.removeEventListener('keydown', resumeAudio); };
  }, []);

  // Fallback: if roundResult is shown but no next round arrives within 12s, something went wrong
  useEffect(() => {
    if (roundResult && !gameResult) {
      const fallbackTimer = setTimeout(() => {
        console.warn('Round end stuck - no next round received after 12s');
        // Force the game to check state - if we're still on roundResult, the server may have lost track
      }, 12000);
      return () => clearTimeout(fallbackTimer);
    }
  }, [roundResult, gameResult]);
  
  // Confetti à la fin de la partie
  useEffect(() => {
    if (gameResult && gameResult.winner.playerId === player?.id && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [gameResult, player]);
  
  // Effet sonore quand on trouve une réponse
  useEffect(() => {
    if (myAnswer) {
      // Vérifier si on vient de trouver quelque chose de nouveau
      if (myAnswer.titleFound && !prevTitleFound) {
        playSuccess();
        setLastAnswerCorrect(true);
        setPrevTitleFound(true);
        
        // Confetti léger
        if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) confetti({
          particleCount: 30,
          spread: 50,
          origin: { y: 0.7 },
          colors: ['#6366f1', '#a855f7', '#22c55e']
        });
      }
      
      if (myAnswer.artistFound && !prevArtistFound) {
        playSuccess();
        setLastAnswerCorrect(true);
        setPrevArtistFound(true);
        
        // Confetti léger
        if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) confetti({
          particleCount: 30,
          spread: 50,
          origin: { y: 0.7 },
          colors: ['#6366f1', '#a855f7', '#22c55e']
        });
      }
    }
  }, [myAnswer?.titleFound, myAnswer?.artistFound, prevTitleFound, prevArtistFound, playSuccess]);
  
  // Un room:left confirmé est la seule autorisation de quitter vers le catalogue.
  useEffect(() => {
    if ((themeLeave === 'pending' || themeLeave === 'error') && hasLeftRoom && !currentRoom) {
      resetGame();
      navigate('/#quick-themes', { replace: true });
    }
  }, [themeLeave, hasLeftRoom, currentRoom, resetGame, navigate]);

  useEffect(() => {
    if (themeLeave !== 'pending') return;
    if (!isConnected) { setThemeLeave('error'); return; }
    const timer = setTimeout(() => setThemeLeave('error'), 12000);
    return () => clearTimeout(timer);
  }, [themeLeave, isConnected]);

  // Retour au lobby si pas de room
  useEffect(() => {
    if (!currentRoom && !gameResult && themeLeave === 'idle') {
      navigate('/', { replace: true });
    }
  }, [currentRoom, gameResult, themeLeave, navigate]);
  
  const handleSubmitAnswer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim() || answerFeedback?.status === 'pending') return;
    
    // Si gelé, bloquer
    if (isFrozen) return;
    
    // Si déjà tout trouvé, bloquer
    if (myAnswer?.titleFound && myAnswer?.artistFound) return;
    
    submitAnswer(answer.trim());
    setAnswer('');
  };

  useEffect(() => {
    if (answerFeedback?.status !== 'incorrect') return;
    setShakeInput(true); setLastAnswerCorrect(false); playError();
    const timer = setTimeout(() => { setShakeInput(false); setLastAnswerCorrect(null); }, 500);
    return () => clearTimeout(timer);
  }, [answerFeedback?.requestId, answerFeedback?.status]);

  const handleChangeTheme = () => {
    if (themeLeave === 'pending') return;
    if (!currentRoom) {
      setThemeLeave('departed');
      resetGame();
      navigate('/#quick-themes', { replace: true });
      return;
    }
    if (!isConnected) { setThemeLeave('error'); return; }
    setThemeLeave('pending');
    leaveRoom();
  };

  const handleBackToLobby = () => {
    resetGame();
    if (code) {
      navigate(`/room/${code}`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };
  
  // Persistent audio element - rendered before all conditional returns so it's always in the DOM
  const audioElement = (
    <>
    {currentRound && <p className="text-dark-300 text-center mb-3">Trouve le titre ou l’artiste</p>}
    {currentRound && answerFeedback && <p role="status" className="text-center mb-3">{answerFeedback.status === 'pending' ? 'Vérification en cours…' : answerFeedback.status === 'unconfirmed' ? 'Réponse non confirmée : vérifie ta connexion puis réessaie.' : answerFeedback.status === 'incorrect' ? 'Réponse incorrecte.' : 'Bonne réponse !'}</p>}
    <div className="mb-4 flex flex-wrap justify-center items-center gap-3 text-sm">
      <RoomVuMeter audioRef={audioRef} analyserRef={analyserRef} enabled={!!currentRound && !showCountdown && !roundResult && !gameResult && !currentRound.isDeafMode} muted={isMuted || volume <= 0}/>
      <button className="btn-secondary" onClick={async () => {
        let played = false;
        try { played = await playTestTone(getAudioContext()); } catch { /* Audio API unavailable */ }
        setToneStatus(played ? 'Bip demandé. Si tu ne l’entends pas, vérifie le volume et la sortie audio.' : 'Son bloqué. Clique pour réessayer.');
      }}>Tester le son</button>
      {toneStatus && <span role="status">{toneStatus}</span>}
      {currentRound && !currentRound.isDeafMode && audioStatus === 'blocked' && <button className="btn-primary" disabled={showCountdown} onClick={() => void startMedia(currentRound, true)}>Activer / réessayer le son</button>}
      {audioStatus === 'unavailable' && <span role="status">Extrait indisponible pour ce round.</span>}
      {audioStatus === 'blocked' && <span role="status">Lecture bloquée ou erreur réseau. Réessaie le son.</span>}
    </div>
    <audio 
      ref={audioRef}
      crossOrigin="anonymous"
      preload="auto"
      onLoadedData={() => {
        if (audioRef.current) {
          audioRef.current.volume = isMuted ? 0 : volume;
        }
      }}
      onPlay={() => {
        if (audioRef.current) {
          audioRef.current.volume = isMuted ? 0 : volume;
        }
      }}
      onError={() => { if (activeRoundRef.current && !activeRoundRef.current.isDeafMode) setAudioStatus('blocked'); }}
      onStalled={() => { if (activeRoundRef.current && !activeRoundRef.current.isDeafMode) setAudioStatus('blocked'); }}
    />
    </>
  );
  
  // Auth guard
  if (!isAuthenticated || !player) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        {audioElement}
        <h1 className="text-2xl font-bold mb-4">Connexion requise</h1>
        <p className="text-dark-400 mb-6">Vous devez être connecté pour accéder à cette page.</p>
        <Link to="/" className="btn-primary">Retour à l'accueil</Link>
      </div>
    );
  }

  // Les résultats utilisent exclusivement les données et confirmations reçues du serveur.
  if (gameResult) {
    const saved = gameResult.stats.gameId ? resultSaves[gameResult.stats.gameId] : undefined;
    const resultScores = gameResult.scores;
    const share = async () => {
      const own = resultScores.find(s => s.playerId === resultPlayerId);
      const invitation = code && /^[A-Z0-9]{4,6}$/.test(code.toUpperCase()) ? `\n${buildRoomInvitation(code, "https://theblindtest.ch")}` : '';
      const message = `Ma partie TheBlindTest${own ? ` : ${own.points} points, ${own.correctTitles} titres et ${own.correctArtists} artistes trouvés` : ''}.${invitation}`;
      try { await navigator.clipboard.writeText(message); setShareFallback(''); setShareCopied(true); } catch { setShareCopied(false); setShareFallback(message); }
    };
    return <div className="scene-game-page">{audioElement}<SceneResultView scores={resultScores} playerId={resultPlayerId} guest={resultWasGuest} save={saved} persistence={gameResult.stats.persistence} onReplay={handleBackToLobby} onChangeTheme={handleChangeTheme} themeLeave={themeLeave} onShare={() => void share()} onRegister={() => setShowRegister(true)} canRegister={player.type === 'guest'} shareFallback={shareFallback} shareCopied={shareCopied}/><AuthModal isOpen={showRegister} onClose={() => setShowRegister(false)} initialMode="register" /></div>;
  }
  if (roundResult) return <div className="scene-game-page">{audioElement}<SceneRoundView result={roundResult} playerId={player.id}/></div>;

  // Écran d'attente des joueurs
  if (isWaitingForPlayers) {
    return (
      <div className="max-w-4xl mx-auto text-center flex flex-col items-center justify-center min-h-[60vh]">
        {audioElement}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-6xl mb-8"
          >
            <MusicIcon name="note"/>
          </motion.div>
          
          <h1 className="font-display text-3xl font-bold mb-4 bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
            Préparation de la partie...
          </h1>
          
          <p className="text-dark-400 mb-8">
            En attente que tous les joueurs soient prêts
          </p>
          
          {/* Liste des joueurs */}
          <div className="card inline-block min-w-[300px] mb-8">
            <h2 className="font-semibold mb-4 flex items-center justify-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              Joueurs ({currentRoom?.players.length || 0})
            </h2>
            <div className="space-y-2">
              {currentRoom?.players.map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    p.id === player?.id ? 'bg-primary-500/20 border border-primary-500' : 'bg-white/5'
                  }`}
                >
                  {p.avatar ? (
                    <img src={p.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-bold">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="font-medium">{p.name}</span>
                  {p.id === currentRoom?.hostId && (
                    <span className="ml-auto text-xs bg-primary-500/30 px-2 py-1 rounded">Host</span>
                  )}
                  <motion.div
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                    className="w-2 h-2 rounded-full bg-green-500 ml-auto"
                  />
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Animation de chargement */}
          <div className="flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-3 h-3 rounded-full bg-primary-500"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>
          
          <motion.p
            className="text-dark-500 text-sm mt-4"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Le jeu commence dans quelques instants...
          </motion.p>
        </motion.div>
      </div>
    );
  }
  
  // Écran de countdown
  if (showCountdown) {
    return (
      <div className="max-w-4xl mx-auto text-center flex items-center justify-center min-h-[60vh]">
        {audioElement}
        <AnimatePresence mode="wait">
          <motion.div
            key={countdown}
            initial={{ scale: 2, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            {countdown > 0 ? (
              <>
                <div className="text-8xl font-display font-bold bg-gradient-to-br from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                  {countdown}
                </div>
                <p className="text-dark-400 mt-4 text-lg">Préparez-vous...</p>
              </>
            ) : (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-6xl"
              >
                <MusicIcon name="note"/>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }
  
  // Écran de jeu principal
  return (
    <div className="scene-game-page">
      {audioElement}
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <p className="text-dark-400">
              Round {currentRound?.round || 1} / {currentRound?.totalRounds || '?'}
            </p>
            {gameMode && gameMode !== 'classic' && (
              <GameModeBadge mode={gameMode} size="sm" />
            )}
          </div>
          <h1 className="font-display text-2xl font-bold">{currentRoom && <RoomName name={currentRoom.name} isAutoRoom={currentRoom.isAutoRoom}/>}</h1>
        </div>
        
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          {/* Contrôle du volume */}
          <div className="flex items-center gap-2">
            <button 
              aria-label={isMuted ? "Activer le son" : "Couper le son"}
              onClick={toggleMute}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={isMuted ? 0 : sliderValue}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-24 h-2 bg-dark-700 rounded-lg appearance-none cursor-pointer accent-primary-500"
              aria-label="Volume"
              title={`Volume: ${Math.round(sliderValue)}%`}
            />
          </div>
          
          {/* Timer */}
          <div className={`flex items-center gap-2 text-lg font-mono ${timeLeft <= 10 ? 'text-error animate-pulse' : 'text-dark-300'}`}>
            <Clock className="w-5 h-5" />
            {timeLeft}s
          </div>
          
          {/* Bouton Power-ups */}
          <motion.button
            aria-label="Power-ups"
            onClick={() => setShowPowerUps(true)}
            className="btn-secondary px-3 py-2 flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isFrozen}
          >
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <span className="hidden sm:inline">Power-ups</span>
          </motion.button>
        </div>
      </div>
      
      {/* Barre de progression du temps */}
      <div className="h-2 bg-dark-700 rounded-full mb-6 overflow-hidden">
        <motion.div 
          className={`h-full rounded-full transition-colors duration-300 ${
            progress > 50 ? 'bg-gradient-to-r from-green-500 to-green-400' :
            progress > 25 ? 'bg-gradient-to-r from-yellow-500 to-orange-400' :
            'bg-gradient-to-r from-red-500 to-red-400'
          }`}
          style={{ width: `${progress}%` }}
          initial={{ width: '100%' }}
        />
      </div>
      
      <div className="scene-game-grid">
      {/* Zone de jeu principale */}
      <div className="scene-round-main"><div className="scene-game-eyebrow">EXTRAIT {currentRound?.round} / {currentRound?.totalRounds}</div><h2 className="scene-round-title">Quel est ce morceau ?</h2><p className="scene-round-subtitle">Écoute. Laisse revenir le refrain.</p>
      <motion.div 
        className={`scene-listening card text-center py-12 mb-6 transition-all duration-300 ${
          lastAnswerCorrect === false ? 'border-red-500 bg-red-500/5' : 
          lastAnswerCorrect === true ? 'border-green-500 bg-green-500/5' : ''
        } ${isEliminated ? 'opacity-50' : ''}`}
        animate={shakeInput ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
      >
        {/* Battle Royale: Afficher les vies */}
        {modeConfig?.hasLives && (
          <BattleRoyaleStatus />
        )}
        
        {/* Teams: Afficher les scores d'équipe */}
        {modeConfig?.teamBased && (
          <TeamScoreDisplay />
        )}
        
        {/* Deaf Test: YouTube video embed (muted) OR Audio Visualizer */}
        {currentRound?.isDeafMode && currentRound?.youtubeVideoId ? (
          <div className="relative w-full aspect-video max-w-2xl mx-auto rounded-xl overflow-hidden bg-dark-900 mb-6">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${currentRound.youtubeVideoId}?autoplay=1&mute=1&start=${currentRound.videoStartTime || 30}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&fs=0&origin=${encodeURIComponent(window.location.origin)}`}
              className="w-full h-full"
              allow="autoplay; encrypted-media"
              style={{ pointerEvents: 'none' }}
            />
            <div className="absolute top-3 right-3 bg-black/60 rounded-full px-3 py-1 flex items-center gap-2 text-white text-xs">
              <VolumeX className="w-4 h-4" />
              DEAF TEST
            </div>
          </div>
        ) : (
          <div className="scene-record"><span aria-hidden="true">?</span><strong aria-label={`${timeLeft} secondes restantes`}>{timeLeft}</strong></div>
        )}
        
        {/* Message d'élimination */}
        {isEliminated ? (
          <div className="text-center py-8">
            <Skull className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-red-400 mb-2">Éliminé !</h2>
            <p className="text-dark-400">Attendez la fin de la partie...</p>
          </div>
        ) : modeConfig?.qcmMode && currentQuestion ? (
          /* Mode Quiz: Afficher les choix multiples */
          <QuizMode onAnswer={submitQuizAnswer} />
        ) : (
          /* Mode classique: Input texte */
          <>
            <p className="text-dark-400 mb-6">
              <MusicIcon name="note"/> Écoutez et devinez la musique !
            </p>
            
            {/* Indicateurs de réponse */}
            <div className="flex justify-center gap-4 mb-6">
              <motion.div 
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  myAnswer?.titleFound 
                    ? 'bg-success/20 text-success border border-success' 
                    : 'bg-white/5 text-dark-400'
                }`}
                animate={myAnswer?.titleFound ? { scale: [1, 1.1, 1] } : {}}
              >
                {myAnswer?.titleFound ? <Check className="w-5 h-5" /> : <Music className="w-5 h-5" />}
                <span>Titre</span>
              </motion.div>
              <motion.div 
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  myAnswer?.artistFound 
                    ? 'bg-success/20 text-success border border-success' 
                    : 'bg-white/5 text-dark-400'
                }`}
                animate={myAnswer?.artistFound ? { scale: [1, 1.1, 1] } : {}}
              >
                {myAnswer?.artistFound ? <Check className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                <span>Artiste</span>
              </motion.div>
            </div>
            
            {/* Input réponse */}
            <form onSubmit={handleSubmitAnswer} className="max-w-md mx-auto">
              <motion.div 
                className="flex gap-2"
                animate={shakeInput ? { x: [-5, 5, -5, 5, 0] } : {}}
              >
                <input
                  ref={inputRef}
                  type="text"
                  aria-label="Titre ou artiste"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={
                    myAnswer?.titleFound && myAnswer?.artistFound 
                      ? "Tout trouvé !" 
                      : myAnswer?.titleFound 
                        ? "Cherchez l'artiste..." 
                        : myAnswer?.artistFound 
                          ? "Cherchez le titre..." 
                          : "Titre ou artiste..."
                  }
                  className={`input-field flex-1 text-center text-lg transition-all ${
                    shakeInput ? 'border-red-500 bg-red-500/10' : ''
                  } ${
                    myAnswer?.titleFound && myAnswer?.artistFound ? 'bg-green-500/10 border-green-500' : ''
                  }`}
                  disabled={myAnswer?.titleFound && myAnswer?.artistFound}
                  autoComplete="off"
                />
                <motion.button
                  type="submit"
                  aria-label="Envoyer la réponse"
                  className="btn-primary px-6"
                  disabled={answerFeedback?.status === 'pending' || !answer.trim() || (myAnswer?.titleFound && myAnswer?.artistFound)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Send className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </form>
            
            {myAnswer?.titleFound && myAnswer?.artistFound && (
              <motion.p 
                className="text-success mt-4 font-semibold"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  <MusicIcon name="star"/>
                </motion.span>
                {' '}Bravo ! Vous avez tout trouvé !{' '}
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, delay: 0.25 }}
                >
                  <MusicIcon name="star"/>
                </motion.span>
              </motion.p>
            )}
          </>
        )}
      </motion.div>
      
      </div>
      <ScenePlayerBoard scores={scores} players={currentRoom?.players || []} answers={playerAnswers} playerId={player?.id} />
      </div>

      {/* Power-ups Menu */}
      <PowerUpsMenu 
        isOpen={showPowerUps}
        onClose={() => setShowPowerUps(false)}
        players={currentRoom?.players.map(p => ({ id: p.id, name: p.name })) || []}
        myId={player?.id || ''}
      />
      
      {/* Power-up Effects (gel, notifications, indices) */}
      <PowerUpEffects />
    </div>
  );
}
